import { App, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { ActionsDropdown as ActionsDropdownBase } from '@shared/components/ActionsDropdown';
import { useDownloadQuotationLog, useDownloadRpaTrace } from '@features/quotations/hooks/useQuotations';
import { ExecutionDetailsModal } from '@features/quotations/components/ExecutionDetailsModal';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { showLoadingNotification } from '@shared/utils/showLoadingNotification';
import { useModal } from '@shared/hooks/useModal';
import { getMenuItems } from './getMenuItems';

const { Link, Text } = Typography;

const PLAYWRIGHT_TRACE_VIEWER_URL = 'https://trace.playwright.dev/';

export const ActionsDropdown = ({ record, userProfileId }) => {
  const { notification } = App.useApp();
  const isSupportUser = useSelector((state) => state.initialData.user?.isSupportUser ?? false);
  const getLogMutation = useDownloadQuotationLog();
  const getRpaTraceMutation = useDownloadRpaTrace();
  const executionDetailsModal = useModal();

  const handleViewPdf = () => {
    window.open(record.complete_public_url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadRequestsResponses = async () => {
    if (getLogMutation.isPending) return;

    const notificationKey = showLoadingNotification({ notification, description: 'Descargando requests-responses...' });

    try {
      await getLogMutation.mutateAsync({
        id: record.quotation,
        quotationInsuranceCarrierId: record.id,
      });

      notification.success({
        key: notificationKey,
        message: <Text strong>Requests-Responses descargado</Text>,
        description: <>¡El <Text italic>requests-responses</Text> se ha descargado correctamente!</>,
      });
    } catch (error) {
      notification.error({
        key: notificationKey,
        message: <Text strong>Requests-Responses no descargado</Text>,
        description: getErrorMessage(error),
      });
    }
  };

  const handleDownloadRpaTrace = async () => {
    if (getRpaTraceMutation.isPending) return;

    const notificationKey = showLoadingNotification({ notification, description: 'Descargando trace RPA...' });

    try {
      await getRpaTraceMutation.mutateAsync({ id: record.quotation, quotationInsuranceCarrierId: record.id });

      notification.success({
        key: notificationKey,
        message: <Text strong>Trace RPA descargado</Text>,
        description: (<>
          El trace RPA se descargó correctamente. Para visualizarlo, abra{' '}
          <Link href={PLAYWRIGHT_TRACE_VIEWER_URL} target='_blank' rel='noopener noreferrer'>
            trace.playwright.dev
          </Link>
          {' '}y arrastre el archivo ZIP descargado.
        </>),
      });
    } catch (error) {
      notification.error({
        key: notificationKey,
        message: <Text strong>Trace RPA no descargado</Text>,
        description: getErrorMessage(error),
      });
    }
  };

  const handleOpenExecutionDetails = () => {
    executionDetailsModal.open();
  };

  const actions = {
    viewPdf: {
      handler: handleViewPdf,
      loading: false,
    },
    downloadRequestsResponses: {
      handler: handleDownloadRequestsResponses,
      loading: getLogMutation.isPending,
    },
    downloadRpaTrace: {
      handler: handleDownloadRpaTrace,
      loading: getRpaTraceMutation.isPending,
    },
    viewExecutionDetails: {
      handler: handleOpenExecutionDetails,
      loading: false,
    },
  };

  const items = getMenuItems({ record, userProfileId, isSupportUser, actions });

  return (
    <>
      <ActionsDropdownBase
        items={items}
        buttonProps={{
          size: 'large',
          style: { position: 'absolute', top: 4, right: 4 }, // Ajusta la posición del botón de acción en la esquina superior derecha
        }}
      />
      <ExecutionDetailsModal
        open={executionDetailsModal.isOpen}
        onClose={executionDetailsModal.close}
        quotationId={record.quotation}
        quotationInsuranceCarrierId={record.id}
      />
    </>
  );
};
